import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquareText } from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import {
  ASK_QUESTION_MAX_CHARS,
  type AskProjectResult,
  type AskSource,
  type Job,
} from '@allemnii-pulse/shared';
import { api } from '../../lib/api.js';
import { useDocument } from '../../lib/firestore.js';
import { Button } from '../../components/ui/Button.js';
import { Textarea } from '../../components/ui/Textarea.js';
import { Checkbox } from '../../components/ui/Checkbox.js';
import { Label } from '../../components/ui/Label.js';
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/Dialog.js';

interface Exchange {
  question: string;
  includeDiscussions: boolean;
  result: AskProjectResult | null;
  error: string | null;
}

const EXAMPLES = [
  'What did we decide about authentication?',
  'Why is anything blocked right now?',
  'Which tasks cover the first user story?',
];

function sourceHref(projectId: string, source: AskSource): string {
  if (source.kind === 'prd') return `/projects/${projectId}/prd`;
  if (source.kind === 'tdd') return `/projects/${projectId}/tdd`;
  if (source.kind === 'discussion') {
    return `/projects/${projectId}/discussions${source.messageId ? `?m=${source.messageId}` : ''}`;
  }
  return `/projects/${projectId}/tasks/${source.taskId}`;
}

function Answer({ projectId, result }: { projectId: string; result: AskProjectResult }) {
  const html = DOMPurify.sanitize(
    marked.parse(result.answer, { async: false, breaks: true, gfm: true }),
  );
  return (
    <div className="flex flex-col gap-2 rounded-md border border-border bg-surface-2 p-3">
      <div className="doc-prose text-sm" dangerouslySetInnerHTML={{ __html: html }} />
      {result.sources.length > 0 && (
        <ol className="flex flex-col gap-0.5 border-t border-border pt-2 text-xs text-muted">
          {result.sources.map((s) => (
            <li key={s.n} className="flex gap-1.5">
              <span className="tabular-nums">[{s.n}]</span>
              <Link to={sourceHref(projectId, s)} className="min-w-0 truncate hover:text-accent">
                {s.label}
              </Link>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/** "Ask this project" — a question answered server-side (POST .../ask) from the PRD, TDD, tasks,
 * comments and blockers — and, when toggled on, the project's discussion messages — with numbered
 * citations. The thread lives only while the dialog is open; each question is answered on its
 * own, without the earlier ones as context. */
export function AskProjectDialog({
  open,
  onOpenChange,
  projectId,
  canIncludeDiscussions,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  /** The asker may read discussions (`discussion.participate`); otherwise no toggle is shown. */
  canIncludeDiscussions: boolean;
}) {
  const [question, setQuestion] = useState('');
  const [includeDiscussions, setIncludeDiscussions] = useState(false);
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [jobId, setJobId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const { data: job } = useDocument<Job>(jobId ? `jobs/${jobId}` : null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const settle = (patch: Partial<Exchange>) =>
    setExchanges((list) => list.map((e, i) => (i === list.length - 1 ? { ...e, ...patch } : e)));

  useEffect(() => {
    if (job?.status === 'succeeded' && job.result) {
      settle({ result: job.result as AskProjectResult });
      setJobId(null);
    } else if (job?.status === 'failed') {
      settle({ error: job.error ?? 'Could not answer that. Please try again.' });
      setJobId(null);
    }
  }, [job]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [exchanges]);

  const working = starting || jobId !== null;
  const trimmed = question.trim();

  async function ask(e?: FormEvent) {
    e?.preventDefault();
    if (!trimmed || working) return;
    const withDiscussions = canIncludeDiscussions && includeDiscussions;
    setExchanges((list) => [
      ...list,
      { question: trimmed, includeDiscussions: withDiscussions, result: null, error: null },
    ]);
    setQuestion('');
    setStarting(true);
    try {
      const { jobId: id } = await api.post<{ jobId: string }>(`/projects/${projectId}/ask`, {
        question: trimmed,
        includeDiscussions: withDiscussions,
      });
      setJobId(id);
    } catch {
      settle({ error: 'Could not send the question. Please try again.' });
    } finally {
      setStarting(false);
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void ask();
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>Ask this project</DialogTitle>
        <DialogDescription>
          Answers come from the accepted PRD and TDD, the tasks, and their comments and blockers
          {canIncludeDiscussions && includeDiscussions ? ', plus the team discussion' : ''}, with
          links to each source.
        </DialogDescription>
      </DialogHeader>

      <div className="mt-4 flex max-h-[55dvh] flex-col gap-4 overflow-y-auto pr-1">
        {exchanges.length === 0 && (
          <div className="flex flex-wrap gap-2">
            {EXAMPLES.map((example) => (
              <Button
                key={example}
                size="sm"
                variant="outline"
                // Buttons don't wrap their label by default; these are sentence-long and would
                // overflow a phone-width dialog.
                className="h-auto min-h-7 whitespace-normal py-1 text-left"
                onClick={() => setQuestion(example)}
              >
                {example}
              </Button>
            ))}
          </div>
        )}
        {exchanges.map((exchange, i) => (
          <div key={i} className="flex flex-col gap-2">
            <p className="max-w-full self-end break-words rounded-md bg-accent/10 px-3 py-2 text-sm">
              {exchange.question}
            </p>
            {exchange.includeDiscussions && (
              <p className="-mt-1 self-end text-[11px] text-muted">Including discussions</p>
            )}
            {exchange.result && <Answer projectId={projectId} result={exchange.result} />}
            {exchange.error && <p className="text-sm text-danger">{exchange.error}</p>}
            {!exchange.result && !exchange.error && (
              <p className="text-xs text-muted">Reading the project…</p>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={(e) => void ask(e)} className="mt-4 flex items-end gap-2">
        <Textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={onKeyDown}
          maxLength={ASK_QUESTION_MAX_CHARS}
          rows={2}
          placeholder="Ask about decisions, scope, blockers, who's doing what…"
          className="min-w-0 flex-1 resize-none"
        />
        <Button
          type="submit"
          variant="default"
          className="h-10 shrink-0 sm:h-8"
          disabled={!trimmed || working}
        >
          <MessageSquareText className="mr-1.5 h-3.5 w-3.5" />
          {working ? 'Thinking…' : 'Ask'}
        </Button>
      </form>
      {canIncludeDiscussions && (
        <Label
          htmlFor="ask-include-discussions"
          className="mt-2 flex w-fit items-center gap-1.5 text-xs font-normal text-muted"
        >
          <Checkbox
            id="ask-include-discussions"
            checked={includeDiscussions}
            onCheckedChange={(c) => setIncludeDiscussions(c === true)}
          />
          Include discussions
        </Label>
      )}
    </Dialog>
  );
}
